import type { DeepReadonly } from 'vue';

import type {
  HotelIngredient,
  RecipeIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { UserLocation } from '@apps/main/new-desktop/core/models';

import type { RouteWithDetails } from '@libs/maps';
import type {
  ActivityStaticInfo,
  ActivitySuggestionWithStaticInfo,
  CarrierName,
  ChronologicalItinerary,
  FerryTicketCategory,
  FullStars,
  IngredientId,
  Pax,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import type { AppSvgIconType } from '@libs/ui';

export const enum ProductCardTopBarType {
  Suggestion = 'suggestion',
  Cheapest = 'cheapest',
  Selected = 'selected',
}

export interface ProductRating {
  stars: FullStars;
}

export interface ProductInfo {
  label: string;
  description: string;
}

export interface PortDetails {
  time: string;
  name: string;
  code?: string;
  country?: string;
}

export interface CarrierLogo {
  urls: string[];
}

export interface ProductTagWithIcon {
  description: string;
  svgIconType: AppSvgIconType;
}

export interface TripOverviewSegments {
  chronologicalItinerary: ChronologicalItinerary[];
  startLocation: UserLocation | undefined;
  endLocation: UserLocation | undefined;
  getIngredient: (id: string) => RecipeIngredient;
  getIngredientIndex: (id: string) => number;
  paxes: DeepReadonly<Pax>;
  isSuggestedHotel: (ingredientId: IngredientId, hotelCode: string) => boolean;
  isSuggestedTransport: (ingredientId: IngredientId) => boolean;
  endLocationIngredient: HotelIngredient | undefined;
  calculatedRoute: RouteWithDetails;
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined;
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined;
  ferryVehicleTypes: TransportVehicle[] | undefined;
  areThereActivitiesInDestination: (destinationId: string) => boolean;
  getActivitySuggestions: (
    destinationId: IngredientId
  ) => ActivitySuggestionWithStaticInfo[];
  getStaticActivitySuggestions: (
    destinationId: IngredientId
  ) => ActivityStaticInfo[];
  selectedDate?: Date;
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  isUserFromBookingPage: boolean;
  hasUserSelectedTransport: boolean;
  isUserFromStena: boolean;
  currency: string;
  moneyFormanterFn: (amount: number, currency: string) => string;
  allowActivitiesUpsellBannerInOwnStay: boolean;
  shouldMoveUpsellAfterTransport: boolean;
}

export const enum UnavailabilityReason {
  SelectedDate = 'SelectedDate',
  SelectedFilters = 'SelectedFilters',
  SelectedProduct = 'SelectedProduct',
  Suggested = 'Suggested',
  SuggestedFromHotelCodeSearch = 'SuggestedFromHotelCodeSearch',
}
